import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';

// Firebase configuration (same as main app)
const firebaseConfig = {
  apiKey: "AIzaSyB3kv6ngK0oUMj9iLp1vEfr_vnVBfhl3B8",
  authDomain: "dierend-e3e72.firebaseapp.com",
  projectId: "dierend-e3e72",
  storageBucket: "dierend-e3e72.firebasestorage.app",
  messagingSenderId: "345320618360",
  appId: "1:345320618360:web:15ae8c88dfb235f8bf708d",
  measurementId: "G-BVZ1QNSKZD"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const functions = getFunctions(app);

// ─────────────────────────────────────────────────────────────────────────────
// ⚠️  BOOTSTRAP ADMIN ALLOWLIST — KEEP IN SYNC ACROSS 3 PLACES
// ─────────────────────────────────────────────────────────────────────────────
// Any change to this list MUST be mirrored in:
//   1. functions/index.js → ADMIN_PANEL_EMAILS  (Cloud Functions auth gate)
//   2. firestore.rules    → isAdmin() function   (database access)
// Forgetting any of the three creates an admin who can sign in but is locked out
// of half the features. There's no DRY way to share these because each lives in
// a different runtime.
// ─────────────────────────────────────────────────────────────────────────────
export const ADMIN_BOOTSTRAP_EMAILS = [
  'admin@dierend.com',
  'support@dierend.com',
  'info@dierend.com',
];

export const isAdminEmail = (email) => {
  return ADMIN_BOOTSTRAP_EMAILS.includes(email?.toLowerCase());
};

/**
 * Resolve admin status for a Firebase user, matching firestore.rules.isAdmin():
 *   1. email is in the bootstrap allowlist AND verified, OR
 *   2. ID token has admin === true custom claim, OR
 *   3. users/{uid}.isAdmin === true.
 * Also returns whether the user has TOTP 2FA enabled.
 * Returns { isAdmin, source, totpEnabled }.
 */
export const resolveAdminStatus = async (firebaseUser) => {
  if (!firebaseUser) return { isAdmin: false, source: null, totpEnabled: false };

  let userDocData = null;
  try {
    const userSnap = await getDoc(doc(db, 'users', firebaseUser.uid));
    if (userSnap.exists()) userDocData = userSnap.data();
  } catch (err) {
    console.warn('Failed to read user doc for admin check:', err);
  }
  const totpEnabled = userDocData?.totpEnabled === true;

  // Mirrors the server rule: the allowlist only counts for a verified email.
  if (firebaseUser.emailVerified === true && isAdminEmail(firebaseUser.email)) {
    return { isAdmin: true, source: 'allowlist', totpEnabled };
  }
  try {
    const tokenResult = await firebaseUser.getIdTokenResult();
    if (tokenResult.claims?.admin === true) {
      return { isAdmin: true, source: 'claim', totpEnabled };
    }
  } catch (err) {
    console.warn('Failed to read ID token claims:', err);
  }
  if (userDocData?.isAdmin === true) {
    return { isAdmin: true, source: 'flag', totpEnabled };
  }
  return { isAdmin: false, source: null, totpEnabled };
};

// Auth functions
export const loginAdmin = async (email, password) => {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  const { isAdmin } = await resolveAdminStatus(userCredential.user);
  if (!isAdmin) {
    await signOut(auth);
    throw new Error('Unauthorized: This account does not have admin access');
  }
  return userCredential.user;
};

export const logoutAdmin = async () => {
  await signOut(auth);
};

export const getCurrentAdmin = () => {
  return new Promise((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      unsubscribe();
      if (!user) return resolve(null);
      const { isAdmin } = await resolveAdminStatus(user);
      resolve(isAdmin ? user : null);
    }, reject);
  });
};

export { auth, db, functions, app };

// Firestore helpers
export {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  Timestamp,
  writeBatch
};
